import { formatCurrency } from '@/addons/currency'
import {
  ApiV1SalesDataAttributes,
  ApiV1SalesDataAttributesCapi,
  PromoEnginePreviewResponseDataAttributesItems,
  SaleDataAttributes,
} from '@/api'
import { computed } from 'vue'
import store from '@/store'
import { LoyaltyGetters } from '@/modules/loyalty/store/index'

import { roundDecimals } from '@/addons/adjustments'

export interface PromoEngineDetail {
  promo: string
  type: string
  value: number | string
  start_date: string
  end_date: string
  counter: number | null
  description: string
  id: number
  sku: string
  fidelity_code: string
}

/**
 * Calculates the total discounts applied from promotions in a sales document.
 *
 * @param dati_documenti - The serialized data for the sales document.
 * @param getOnlyDiscount - If true, returns only the total discount amount. If false, returns the formatted discount amount with a leading '-'.
 * @param fromList - If true, calculates the discount from the list of promotions. If false, calculates the discount from the items and costs.
 * @returns The total discount amount from promotions, or `undefined` if no promotions data is available.
 */
export const appliedDiscountsFromPromotions = (
  dati_documenti: string,
  getOnlyDiscount?: boolean,
  fromList?: boolean,
): string | number | undefined => {
  if (dati_documenti?.length > 0) {
    const datiDocumenti = JSON.parse(dati_documenti ?? '{}')
    if (
      datiDocumenti?.promo_engine &&
      Object.keys(datiDocumenti?.promo_engine).length > 0
    ) {
      let promo = 0
      if (fromList) {
        promo = Object.keys(datiDocumenti?.promo_engine.promo).reduce(
          (acc, cur) => {
            const itemPromo = datiDocumenti?.promo_engine.promo[cur]
            const amount = (itemPromo.items || [])?.reduce(
              (r, i) => r + i.amount,
              0,
            )
            return acc + amount
          },
          0,
        )
      }
      const garmentsTotalDiscount = datiDocumenti?.promo_engine.items
        ? datiDocumenti?.promo_engine.items.reduce(
            (pr: number, cur: object) => {
              return cur.discount_on_item ? pr + cur.discount_on_item : pr
            },
            0,
          )
        : 0
      const costsTotalDiscount = datiDocumenti?.promo_engine.costs
        ? datiDocumenti?.promo_engine.costs.reduce(
            (pr: number, cur: object) => {
              return cur.discount_on_item ? pr + cur.discount_on_item : pr
            },
            0,
          )
        : 0
      if (getOnlyDiscount) {
        return parseFloat(garmentsTotalDiscount + costsTotalDiscount + promo)
      }
      return (
        '-' +
        formatCurrency(
          parseFloat(
            garmentsTotalDiscount + costsTotalDiscount + Math.abs(promo),
          ),
          true,
          true,
        )
      )
    }
  }
  return undefined
}

/**
 * Calculates the total discounts per promotion for a given sales document.
 *
 * @param dati_documenti - A string containing the sales document data in JSON format.
 * @returns An array of objects, where each object represents a promotion and contains the promotion name and the total discount amount for that promotion.
 */
export const sumDiscountsPerPromotion = (
  dati_documenti: string,
): Array<{ promoName: string; amount: number }> => {
  if (dati_documenti?.length > 0) {
    const datiDocumenti = JSON.parse(dati_documenti ?? '{}')
    if (
      datiDocumenti?.promo_engine &&
      Object.keys(datiDocumenti?.promo_engine).length > 0
    ) {
      const discountsPerPromo: Record<string, number> = {}

      if (datiDocumenti?.promo_engine.promo) {
        Object.entries(datiDocumenti.promo_engine.promo).forEach(
          ([promoKey, promoValue]: [string, any]) => {
            discountsPerPromo[promoKey] = promoValue.items.reduce(
              (total: number, item: any) => total + item.amount,
              0,
            )
          },
        )
      }

      return Object.entries(discountsPerPromo).map(([promoName, amount]) => ({
        promoName,
        amount: -Math.abs(amount), // Ensure the amount is negative
      }))
    }
  }
  return []
}

/**
 * Retrieves the promotions data for a specific item in a sales document.
 *
 * @param dati_documenti - A string containing the sales document data in JSON format.
 * @param sku - The SKU (Stock Keeping Unit) of the item to retrieve the promotions data for.
 * @returns An array of objects, where each object represents a promotion and contains the promotion name and the total discount amount for that promotion.
 */
export const getPromotionsFromSku = (
  dati_documenti: string,
  sku: string,
): Array<{ promoName: string; amount: number }> => {
  if (dati_documenti?.length > 0) {
    const datiDocumenti = JSON.parse(dati_documenti ?? '{}')
    if (
      datiDocumenti?.promo_engine &&
      Object.keys(datiDocumenti?.promo_engine).length > 0
    ) {
      const item = datiDocumenti.promo_engine.items.find(
        (item) => item.sku === sku,
      )
      if (item && item.promo_engine) {
        return item.promo_engine.map((promoId) => {
          const promo = datiDocumenti.promo_engine.promo[promoId]
          const amount = promo?.items.find((i) => i.id === item.id)?.amount || 0
          return { promoName: promoId, amount: amount }
        })
      }
    }
  }
  return []
}
/**
 * Retrieves the promotions data for a given item based on its `progressivo`.
 * @param progressivo - The progress number of the item.
 * @returns An array of objects, where each object represents a promotion and contains the promotion promoId and the promotion amount.
 */
export const getPromoFromProgressivo = (
  dati_documenti,
  progressivo,
): Array<{ promoName: string; amount: number }> => {
  try {
    const document_data = JSON.parse(dati_documenti)
    const promos = document_data.promo_engine?.promo
    if (!promos) {
      return []
    }

    const result = Object.entries(promos).reduce(
      (acc, [promoId, promo]) => {
        if (
          promo &&
          typeof promo === 'object' &&
          'items' in promo &&
          Array.isArray(promo.items)
        ) {
          const matchingItems = promo.items.filter(
            (promoItem) => promoItem.id === progressivo,
          )

          matchingItems.forEach((item) => {
            acc.push({
              promoName: promoId,
              amount: item.amount,
            })
          })
        }
        return acc
      },
      [] as { promoName: string; amount: number }[],
    )
    return result
  } catch (e) {
    // console.error('Errore nel parsing di dati_documenti:', e)
    return []
  }
}

/**
 * Retrieves the promotions data for a specific item in a sales document.
 *
 * @param sale - The sales data object containing the document information.
 * @param sku - The SKU (Stock Keeping Unit) of the item to retrieve the promotions data for.
 * @returns An array of promotion data objects for the specified item, or `undefined` if no promotions data is available.
 */
export const getPromotionsDataOnItem = (
  sale: ApiV1SalesDataAttributes,
  sku: string,
): [] | undefined => {
  if (Object.keys(sale).length > 0) {
    const datiDocumenti = JSON.parse(sale?.dati_documenti ?? '{}')
    if (
      datiDocumenti?.promo_engine &&
      Object.keys(datiDocumenti?.promo_engine).length > 0 &&
      datiDocumenti?.promo_engine?.items
    ) {
      return datiDocumenti?.promo_engine.items.filter(
        (item: PromoEnginePreviewResponseDataAttributesItems) =>
          item.sku === sku,
      )
    }
  }
  return undefined
}
/**
 * Builds final payload to use as new sale for both MANUAL and AUTO
 *
 * @param currentPreview - preview (manual or auto) store in Pinia
 * @param currentEstimate - input/output object stored in Pinia
 * @param saleData - sale data item
 * @param mode - either 'manual' or 'auto' - TBD
 * @param saleDatiAggiuntivi
 * @param isPromoEngineManualActive
 */
export function buildFinalSalePayloadForPromos(
  currentPreview,
  currentEstimate,
  saleData: SaleDataAttributes,
  mode?: string,
  saleDatiAggiuntivi?: unknown,
  isPromoEngineManualActive?: boolean,
) {
  const adapted = buildSalePromoAttributesFromManualPreview(
    currentPreview,
    currentEstimate,
    saleData?.capi,
    mode,
    isPromoEngineManualActive,
  )
  // dati_documenti
  // 1) parse diretto della sale (stringa → oggetto)
  const saleDocObj = saleData?.dati_documenti
    ? JSON.parse(saleData.dati_documenti)
    : {}

  // 2) prendi l'OGGETTO manuale costruito in adapted
  const manualDocObj = adapted?.dati_documenti || {}
  // 3) normalizza il manual nel formato "auto"
  const normalized = normalizeManualDocToAuto(
    manualDocObj,
    currentPreview?.changes || [],
  ) // { promo_engine: {...} }

  // 4) merge esplicito: il normalizzato vince su sale
  const finalDocObj = {
    promo_engine: {
      ...(saleDocObj.promo_engine || {}),
      ...normalized.promo_engine,
      items: normalized.promo_engine.items,
      promo: normalized.promo_engine.promo,
      vouchers: normalized.promo_engine.vouchers,
      payments: normalized.promo_engine.payments,
      costs: normalized.promo_engine.costs,
      promo_engine_details: normalized.promo_engine.promo_engine_details,
      promo_effects: [], // campo indicato come da NON popolare, aggiungo in caso la mancanza della chiave blocchi la chiusura della vendita
    },
  }

  // 5) fine doc serializzati
  const finalDocStr = JSON.stringify(finalDocObj)
  const finalDatiAggiuntivi = JSON.stringify(saleDatiAggiuntivi)

  // 6) crea la nuova sale
  const fullSale = {
    ...saleData,
    capi: adapted.capi,
    dati_documenti: finalDocStr,
    dati_aggiuntivi: finalDatiAggiuntivi,
  }
  return fullSale
}

// helper per numeri: evita NaN
export function toNumberOrUndefined(v) {
  if (v === null || v === undefined) {
    return undefined
  }
  const n = Number(v)
  return isFinite(n) ? n : undefined
}

export function buildPromoEngineDetailsFromSource(src = {}, changes = []) {
  const details: PromoEngineDetail[] = []
  const enabledCard = computed(
    () => store.getters[LoyaltyGetters.GET_ENABLED_CARD],
  )

  const items = Array.isArray(src.items) ? src.items : []

  for (const item of items) {
    const itemId = item?.id

    const points = Array.isArray(item?.promo_engine_points)
      ? item.promo_engine_points
      : []

    for (const p of points) {
      const promoId = p?.promo_id

      // Trova promo corrente nella lista changes
      const change = changes.find((c) => c.promo === promoId)

      const current_counter = change?.promotion_counter?.current_counter ?? null
      const description = change?.informations?.description ?? ''

      // const max_counter = change?.promotion_counter?.max_counter ?? null

      details.push({
        promo: promoId,
        type: p?.type ?? '',
        value: p?.value ?? '',
        start_date: p?.start_date ?? '',
        end_date: p?.end_date ?? '',
        counter: current_counter, // <-- richiesto
        // max_counter,
        description,
        id: itemId,
        sku: item.sku,
        fidelity_code: enabledCard.value.valore || '',
      })
    }
  }

  return details
}

// NORMALIZZAZIONE MANUAL --> AUTO
function normalizeManualDocToAuto(manualDocObj, changes = []) {
  const doc = manualDocObj || {}
  const src = doc?.promo_engine || {}

  // items: mappa chiavi manual --> auto
  const srcItems = Array.isArray(src.items) ? src.items : []
  const normItems = srcItems.map((it) => {
    const prev_price = toNumberOrUndefined(it?.price_before ?? it?.prev_price)
    const final_price = toNumberOrUndefined(it?.price_after ?? it?.final_price)
    const list_price = toNumberOrUndefined(it?.list_price)

    const net_final_price = final_price !== undefined ? final_price : undefined

    const discount_on_item =
      prev_price !== undefined && final_price !== undefined
        ? roundDecimals(prev_price - final_price)
        : undefined

    const discount_final_price = prev_price ?? list_price

    return {
      id: it?.id,
      sku: it?.sku,
      name: it?.name,
      modified: Boolean(it?.modified),
      promo_engine: Array.isArray(it?.promo_engine) ? it.promo_engine : [],

      // chiavi formato "auto"
      prev_price,
      final_price,
      net_final_price,
      discount_on_item,
      discount_final_price,
      added: Boolean(it?.added),
    }
  })

  // promo: copia amount; percentuale opzionale per item
  const srcPromo = src?.promo && typeof src.promo === 'object' ? src.promo : {}
  const normPromo = {}

  for (const code of Object.keys(srcPromo)) {
    const promoEntry = srcPromo[code] || {}
    const promoItems = Array.isArray(promoEntry.items) ? promoEntry.items : []

    normPromo[code] = {
      items: promoItems.map((p) => {
        let value_type = p?.value_type
        let value = p?.value

        return {
          id: p?.id,
          amount: toNumberOrUndefined(p?.amount),
          value_type,
          value,
        }
      }),
      costs: Array.isArray(promoEntry.costs) ? promoEntry.costs : [],
      title: promoEntry.title ?? '',
      description: promoEntry.description ?? '',
      id_promo: promoEntry.id_promo ?? code,
    }
  }

  // prepariamo i dati per promo_engine_details
  const details = buildPromoEngineDetailsFromSource(src, changes)

  return {
    promo_engine: {
      vouchers: Array.isArray(src.vouchers) ? src.vouchers : [],
      payments: Array.isArray(src.payments) ? src.payments : [],
      costs: Array.isArray(src.costs) ? src.costs : [],
      items: normItems,
      promo: normPromo,
      promo_engine_details: details,
    },
  }
}

// TODO: Spostare in file utility ASAP
// ADAPTER per i dati promo/sale
export function buildSalePromoAttributesFromManualPreview(
  preview: any,
  estimateData?: any,
  saleCapi?: ApiV1SalesDataAttributesCapi[],
  mode?: string,
  isPromoEngineManualActive?: boolean,
) {
  const capi = updateCapiBasedOnPreview(saleCapi, preview)

  // costruiamo mappa promo
  const promoArray = Array.isArray(preview?.promo) ? preview.promo : []

  const validPromos = promoArray.filter((p: any) => {
    return p && p.code
  })

  const promoMap: Record<string, { items: unknown[] }> = {}

  for (const p of validPromos) {
    const code = String(p.code)
    const effects = Array.isArray(p.effects) ? p.effects : []
    const effItems = effects.flatMap((e: any) => e?.items || [])

    promoMap[code] = {
      items: effItems.map((i: any) => {
        // se servono valori assoluti
        // const amount = Math.abs(i?.amount_correction?.end ?? i?.final_price?.diff ?? 0);
        // return { id: i?.id, amount: Math.abs(amount) }
        const amount = i?.amount_correction?.end ?? i?.final_price?.diff ?? 0
        return { id: i?.id, amount: amount, value: i?.value }
      }),
    }
  }

  return {
    capi: capi,
    dati_documenti: {
      promo_engine: {
        mode: mode ?? 'manual',
        rule_engine_version: estimateData?.rule_engine_version || '',
        maxed_out_promotions: estimateData?.maxed_out_promotions || {},
        items: preview?.items || [],
        promo: isPromoEngineManualActive ? promoMap : preview?.promo,
      },
    },
  }
}

// aggiorna prezzi capi in base alla preview aggiornata
export function updateCapiBasedOnPreview(
  saleCapi: ApiV1SalesDataAttributesCapi[],
  preview,
) {
  // mappa sku -> item della preview
  const bySkuItem = new Map(
    (preview?.items || []).map((it: any) => [`${it.sku}-${it.id}`, it]),
  )

  // overlay sui capi ESISTENTI: aggiorna solo i prezzi, preserva progressivo ecc.
  const capi = (saleCapi || []).map((capo: any) => {
    const skuWithId = `${capo.sku}-${capo.progressivo}`
    const it = bySkuItem.get(skuWithId)
    if (!it) {
      return capo
    }
    const priceBefore = it?.price_before ?? it?.prev_price
    const priceAfter = it?.price_after ?? it?.final_price
    return {
      ...capo,
      // ? prezzo_listino: Number(it?.list_price ?? capo.importo_iniziale),
      importo_iniziale: Number(priceBefore ?? capo.importo_iniziale),
      importo_finale: roundDecimals(Number(priceAfter ?? capo.importo_finale)),
    }
  })
  return capi
}
